import * as React from 'react';
import { ComponentPropsWithoutRef, useEffect } from 'react';
import { usePreservedCallback } from '@toss/react';
import { cva } from 'class-variance-authority';
import { cn } from '@zep/utils';

// @types/react 18.3.1 은 enterKeyHint 를 InputHTMLAttributes 에만 선언한다.
// HTML 표준상 모든 요소의 전역 속성이라 textarea 에도 보강한다.
declare module 'react' {
  interface TextareaHTMLAttributes<T> {
    enterKeyHint?: React.InputHTMLAttributes<T>['enterKeyHint'];
  }
}
const inputVariants = cva(['bg-background flex w-full resize-none text-sm !leading-normal placeholder:text-gray-500', 'px-[10px]', 'border focus:border-[#6758FF]', 'rounded-[8px] border-gray-300', 'focus-visible:outline-none',
// 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
'disabled:cursor-not-allowed', 'text-body-2 font-medium text-gray-900'], {
  variants: {
    size: {
      sm: ['max-h-[48px] min-h-[32px] py-[5px] disabled:bg-gray-100'],
      md: ['max-h-[38px] min-h-[38px] py-[8px] disabled:bg-gray-200']
    },
    type: {
      default: ['md:focus:shadow-[0px_0px_0px_1px_rgba(103,88,255,0.20),0px_0px_0px_4px_rgba(103,88,255,0.08)]'],
      ai: ['md:focus:shadow-[0px_0px_0px_1px_rgba(21,176,156,0.20),0px_0px_0px_4px_rgba(21,176,156,0.08)]']
    }
  },
  defaultVariants: {
    size: 'sm',
    type: 'default'
  }
});
export type ChatInputProps = ComponentPropsWithoutRef<'textarea'> & {
  initialHeight?: number;
  maxHeight?: number;
  size?: 'sm' | 'md';
  type?: 'default' | 'ai';
};
export const ChatInput = React.forwardRef<HTMLTextAreaElement, ChatInputProps>(({
  value,
  className,
  onChange,
  initialHeight = 32,
  maxHeight = 48,
  size,
  type,
  ...props
}, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  // 실제 textarea 엘리먼트를 그대로 넘긴다. 스프레드 복제는 own 프로퍼티만
  // 복사해 prototype 메서드(setSelectionRange 등)가 사라진다.
  const assignRef = React.useCallback((node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  const adjustHeight = usePreservedCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = `${initialHeight}px`;
      const newHeight = Math.min(textareaRef.current.scrollHeight + 2, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  });
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);
  return <textarea className={cn(inputVariants({
    size: size,
    type: type
  }), className)} value={value} ref={assignRef} enterKeyHint="send" onChange={e => {
    adjustHeight();
    if (onChange) onChange(e);
  }} {...props} />;
});
ChatInput.displayName = 'ChatInputTextArea';